import { areDatesEqual, isDateLikeValue } from './isDateLikeValue'

/**
 * Computes a deep diff between two objects, returning only the fields that
 * changed — using the values from `payload`.
 *
 * Only keys present in `payload` are evaluated. Keys that exist in
 * `initialValues` but are absent from `payload` are **not** included in the
 * result (i.e. deletions are not detected).
 *
 * ---
 *
 * **Comparison rules (applied in order):**
 *
 * 1. **`null`** — if either side is `null` and the values differ, the
 *    `payload` value is included. Prevents falling through to object/array
 *    recursion with a null operand.
 *
 * 2. **Dates** (`Date` instances or ISO / `YYYY-MM-DD` strings) — compared by
 *    timestamp via `areDatesEqual`, so `new Date('2026-06-09T00:00:00.000Z')`
 *    and `'2026-06-09'` are treated as equal. Prevents `Date` objects from
 *    entering the plain-object recursion branch (`typeof Date === 'object'`).
 *
 * 3. **Arrays** — compared with `JSON.stringify`. If any element differs
 *    (addition, removal, reorder, or mutation), **the entire `payload` array
 *    is included in the result**, even if only one item changed. There is no
 *    element-level diffing.
 *
 * 4. **Plain objects** — recursed into. Only the keys that changed are
 *    included (partial nested diff). An empty nested diff is omitted.
 *
 * 5. **Primitives** (`string`, `number`, `boolean`) — compared with `!==`.
 *
 * ---
 *
 * @param initialValues - The original object (e.g. values fetched from the API).
 * @param payload       - The updated object (e.g. form state on submit).
 * @returns A partial object containing only the changed keys with their new
 *          values from `payload`. Returns `{}` when nothing changed.
 *
 * @example
 * objectDiff(
 *   { name: 'A', points: { type: 'DEFAULT', startDate: '2026-01-01' } },
 *   { name: 'B', points: { type: 'DEFAULT', startDate: '2026-01-01' } }
 * )
 * // → { name: 'B' }
 *
 * @example
 * // Arrays: entire payload array is returned, not just the diff
 * objectDiff(
 *   { steps: ['step 1'] },
 *   { steps: ['step 1', 'step 2'] }
 * )
 * // → { steps: ['step 1', 'step 2'] }
 */
export const objectDiff = <T extends object, U extends object>(
  initialValues: T,
  payload: U
): Record<string, unknown> => {
  const diff: Record<string, unknown> = {}
  const initialRecord = initialValues as Record<string, unknown>
  const payloadRecord = payload as Record<string, unknown>

  Object.entries(payloadRecord).forEach(([key, value]) => {
    const initialValue = initialRecord[key]

    if (value === null || initialValue === null) {
      if (initialValue !== value) {
        diff[key] = value
      }

      return
    }

    if (isDateLikeValue(value) || isDateLikeValue(initialValue)) {
      if (!areDatesEqual(initialValue, value)) {
        diff[key] = value
      }

      return
    }

    if (Array.isArray(value) && Array.isArray(initialValue)) {
      if (JSON.stringify(value) !== JSON.stringify(initialValue)) {
        diff[key] = value
      }

      return
    }

    if (value !== null && typeof value === 'object' && initialValue !== null && typeof initialValue === 'object') {
      const nested = objectDiff(initialValue as Record<string, unknown>, value as Record<string, unknown>)

      if (Object.keys(nested).length > 0) {
        diff[key] = nested
      }

      return
    }

    if (initialValue !== value) {
      diff[key] = value
    }
  })

  return diff
}
